import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Gift, Zap, Search, ChevronDown, Brain } from 'lucide-react';
import { cn } from '@/utils/cn';
import SearchDropdown from '../ui/SearchDropdown';
import CountdownTimer from '../ui/CountdownTimer';
import LearnPathMegaMenu from '../ui/LearnPathMegaMenu';
import { usePromotional } from '../../contexts/PromotionalContext';
import '../../styles/scrollAnimations.css';

const navigation = [
  { name: 'Home', href: '/' },
  { name: 'AI Courses', href: '/courses' },
  { name: 'Automation Courses', href: '/automation' },
  { name: 'Course Bundles', href: '/course-bundles' },
  { name: 'Free Resources', href: '/resources' },
];

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showPromoBanner, setShowPromoBanner] = useState(true);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isAutomationDropdownOpen, setIsAutomationDropdownOpen] = useState(false);
  const [isAiDropdownOpen, setIsAiDropdownOpen] = useState(false);
  const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false);
  const [hoverTimeout, setHoverTimeout] = useState<number | null>(null);
  const { config, isActive: isPromoActive } = usePromotional();
  const location = useLocation();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle click outside to close search and dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.search-container')) {
        setIsSearchOpen(false);
      }
      if (!target.closest('.automation-dropdown')) {
        setIsAutomationDropdownOpen(false);
      }
      if (!target.closest('.ai-dropdown')) {
        setIsAiDropdownOpen(false);
      }
      if (!target.closest('.mega-menu-container')) {
        setIsMegaMenuOpen(false);
      }
    };

    if (isSearchOpen || isAutomationDropdownOpen || isAiDropdownOpen || isMegaMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isSearchOpen, isAutomationDropdownOpen, isAiDropdownOpen, isMegaMenuOpen]);


  const isActive = (href: string) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };


  return (
    <>
      {/* Promotional Top Banner */}
      {showPromoBanner && config.isEnabled && isPromoActive && config.showStickyHeader && (
        <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white py-2 px-4 text-center relative animate-slide-down z-50">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Gift className="h-4 w-4 animate-bounce" aria-label="Gift icon" />
              <span className="font-bold">{config.urgencyMessage}</span>
            </div>
            <div className="hidden sm:flex items-center gap-2">
              <span>Use: <code className="bg-white/20 px-2 py-0.5 rounded font-mono font-bold">{config.couponCode}</code></span>
            </div>
            <CountdownTimer 
              endDate={config.endDate}
              variant="compact"
              className="text-white text-xs"
            />
            <button
              onClick={() => setShowPromoBanner(false)}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 hover:bg-white/20 rounded-full p-1 transition-all"
            >
              <X className="h-4 w-4" aria-label="X icon" />
            </button>
          </div>
        </div>
      )}

      <header className={cn(
        'sticky z-50 transition-all duration-300 safe-top',
        showPromoBanner ? 'top-10' : 'top-0',
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-lg shadow-primary-500/5' 
          : 'bg-white shadow-sm'
      )}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14 sm:h-16">
          {/* Logo - Enhanced */}
          <Link to="/" className="flex items-center space-x-2 sm:space-x-3 group">
            <div className="p-1 sm:p-1.5 rounded-lg sm:rounded-xl shadow-md group-hover:shadow-lg transition-all duration-300 group-hover:scale-105 gpu-accelerated">
              <img 
                src="/executeautomation-logo.svg" 
                alt="ExecuteAutomation Logo" 
                className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg object-cover"
              />
            </div>
            <span className="text-lg sm:text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
              <span className="hidden sm:inline">ExecuteAutomation</span>
              <span className="sm:hidden">EA</span>
            </span>
          </Link>

          {/* Search Icon - Desktop */}
          <div className="hidden lg:flex search-container">
            <button
              onClick={() => setIsSearchOpen(!isSearchOpen)}
              className={cn(
                "p-2 rounded-lg transition-all duration-300",
                isSearchOpen 
                  ? "text-blue-600 bg-blue-50 hover:bg-blue-100" 
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              )}
              aria-label="Search courses"
            >
              <Search className="h-5 w-5" aria-label="Search icon" />
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navigation.map((item) => {
              if (item.name === 'AI Courses') {
                return (
                  <div key={item.name} className="relative ai-dropdown">
                    <button
                      onClick={() => setIsAiDropdownOpen(!isAiDropdownOpen)}
                      className={cn(
                        'flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300 hover-lift',
                        isActive(item.href)
                          ? 'text-primary-600 bg-primary-50 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 hover:shadow-sm'
                      )}
                    >
                      {item.name}
                      <ChevronDown className={cn(
                        "w-4 h-4 transition-transform duration-200",
                        isAiDropdownOpen && "rotate-180"
                      )} />
                    </button>
                    
                    {/* Dropdown Menu */}
                    {isAiDropdownOpen && (
                      <div className="absolute top-full left-0 mt-2 w-64 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden animate-slide-down">
                        <div className="p-2">
                          <Link
                            to="/courses"
                            onClick={() => setIsAiDropdownOpen(false)}
                            className="flex items-start gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors group"
                          >
                            <div className="p-2 bg-purple-100 rounded-lg group-hover:bg-purple-200 transition-colors">
                              <Brain className="w-4 h-4 text-purple-600" aria-label="Brain icon" />
                            </div>
                            <div>
                              <div className="font-medium text-gray-900 text-sm">All Courses</div>
                              <div className="text-xs text-gray-600">Browse AI courses</div>
                            </div>
                          </Link>
                          
                          <div className="border-t border-gray-100 my-2"></div>
                          <div className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">Learning Path</div>
                          
                          <Link
                            to="/paths"
                            onClick={() => setIsAiDropdownOpen(false)}
                            className="flex items-start gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors group"
                          >
                            <div className="p-2 bg-purple-100 rounded-lg group-hover:bg-purple-200 transition-colors">
                              <Brain className="w-4 h-4 text-purple-600" aria-label="Brain icon" />
                            </div>
                            <div>
                              <div className="font-medium text-gray-900 text-sm">AI & Testing Learning Path</div>
                              <div className="text-xs text-gray-600">Complete AI journey</div>
                            </div>
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                );
              }
              
              if (item.name === 'Automation Courses') {
                return (
                  <div key={item.name} className="relative automation-dropdown">
                    <button
                      onClick={() => setIsAutomationDropdownOpen(!isAutomationDropdownOpen)}
                      className={cn(
                        'flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300 hover-lift',
                        isActive(item.href) || location.pathname.startsWith('/paths')
                          ? 'text-primary-600 bg-primary-50 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 hover:shadow-sm'
                      )}
                    >
                      {item.name}
                      <ChevronDown className={cn(
                        "w-4 h-4 transition-transform duration-200",
                        isAutomationDropdownOpen && "rotate-180"
                      )} />
                    </button>
                    
                    {/* Dropdown Menu */}
                    {isAutomationDropdownOpen && (
                      <div className="absolute top-full left-0 mt-2 w-64 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden animate-slide-down">
                        <div className="p-2">
                          <Link
                            to="/automation"
                            onClick={() => setIsAutomationDropdownOpen(false)}
                            className="flex items-start gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors group"
                          >
                            <div className="p-2 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors">
                              <Zap className="w-4 h-4 text-blue-600" aria-label="Zap icon" />
                            </div>
                            <div>
                              <div className="font-medium text-gray-900 text-sm">All Courses</div>
                              <div className="text-xs text-gray-600">Browse automation courses</div>
                            </div>
                          </Link>
                          
                          <div className="border-t border-gray-100 my-2"></div>
                          <div className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">Learning Paths</div>
                          
                          <Link
                            to="/paths/selenium-csharp"
                            onClick={() => setIsAutomationDropdownOpen(false)}
                            className="flex items-start gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors group"
                          >
                            <div className="p-2 bg-green-100 rounded-lg group-hover:bg-green-200 transition-colors">
                              <Zap className="w-4 h-4 text-green-600" aria-label="Zap icon" />
                            </div>
                            <div>
                              <div className="font-medium text-gray-900 text-sm">Selenium C# Mastery</div>
                              <div className="text-xs text-gray-600">4 courses • Beginner to Expert</div>
                            </div>
                          </Link>
                          
                          <Link
                            to="/paths/playwright"
                            onClick={() => setIsAutomationDropdownOpen(false)}
                            className="flex items-start gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors group"
                          >
                            <div className="p-2 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors">
                              <Zap className="w-4 h-4 text-blue-600" aria-label="Zap icon" />
                            </div>
                            <div>
                              <div className="font-medium text-gray-900 text-sm">Playwright Mastery</div>
                              <div className="text-xs text-gray-600">4 courses • Beginner to Expert</div>
                            </div>
                          </Link>
                          
                          <Link
                            to="/paths/selenium-java"
                            onClick={() => setIsAutomationDropdownOpen(false)}
                            className="flex items-start gap-3 p-3 hover:bg-gray-50 rounded-lg transition-colors group"
                          >
                            <div className="p-2 bg-orange-100 rounded-lg group-hover:bg-orange-200 transition-colors">
                              <Zap className="w-4 h-4 text-orange-600" aria-label="Zap icon" />
                            </div>
                            <div>
                              <div className="font-medium text-gray-900 text-sm">Selenium Java Mastery</div>
                              <div className="text-xs text-gray-600">4 courses • Beginner to Expert</div>
                            </div>
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                );
              }
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    'px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300 hover-lift',
                    isActive(item.href)
                      ? 'text-primary-600 bg-primary-50 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 hover:shadow-sm'
                  )}
                >
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* User Actions - Desktop */}
          <div className="hidden lg:flex items-center space-x-3">
            <div 
              className="mega-menu-container relative"
              onMouseEnter={() => {
                if (hoverTimeout) {
                  clearTimeout(hoverTimeout);
                  setHoverTimeout(null);
                }
                setIsMegaMenuOpen(true);
              }}
              onMouseLeave={() => {
                const timeout = setTimeout(() => {
                  setIsMegaMenuOpen(false);
                }, 150);
                setHoverTimeout(timeout);
              }}
            >
              <button className="group px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-purple-600 via-blue-600 to-indigo-600 rounded-xl hover:from-purple-700 hover:via-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 hover:-translate-y-0.5 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="flex items-center gap-2 relative z-10">
                  <Zap className="h-4 w-4 animate-pulse" aria-label="Zap icon" />
                  <span>Learn Path</span>
                </div>
              </button>
              
              {/* Mega Menu */}
              <LearnPathMegaMenu 
                isOpen={isMegaMenuOpen}
                onClose={() => setIsMegaMenuOpen(false)}
              />
            </div>
          </div>

          {/* Mobile menu button - Enhanced */}
          <button
            type="button"
            className="lg:hidden inline-flex items-center justify-center p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-all duration-300 hover-scale focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle mobile menu"
          >
            <div className="relative w-6 h-6">
              <Menu className={`absolute inset-0 h-6 w-6 transition-all duration-300 ${isMenuOpen ? 'opacity-0 rotate-180' : 'opacity-100 rotate-0'}`} />
              <X className={`absolute inset-0 h-6 w-6 transition-all duration-300 ${isMenuOpen ? 'opacity-100 rotate-0' : 'opacity-0 -rotate-180'}`} />
            </div>
          </button>
        </div>

        {/* Mobile Navigation - Enhanced */}
        {isMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 animate-slide-down safe-bottom">
            <div className="px-2 pt-2 pb-4 space-y-1">
              {/* Mobile Search - Enhanced */}
              <div className="px-3 py-2">
                <SearchDropdown className="w-full" aria-label="SearchDropdown icon" />
              </div>

              {/* Navigation Links */}
              {navigation.map((item) => {
                if (item.name === 'AI Courses') {
                  return (
                    <div key={item.name} className="space-y-1">
                      <Link
                        to={item.href}
                        className={cn(
                          'block px-3 py-3 text-base font-medium rounded-lg transition-all duration-300',
                          isActive(item.href)
                            ? 'text-primary-600 bg-primary-50 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 hover:shadow-sm'
                        )}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {item.name}
                      </Link>
                      
                      {/* Learning Path Sub-menu */}
                      <div className="ml-4 space-y-1">
                        <div className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">Learning Path</div>
                        
                        <Link
                          to="/paths"
                          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-all duration-300"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                          AI & Testing Learning Path
                        </Link>
                      </div>
                    </div>
                  );
                }
                
                if (item.name === 'Automation Courses') {
                  return (
                    <div key={item.name} className="space-y-1">
                      <Link
                        to={item.href}
                        className={cn(
                          'block px-3 py-3 text-base font-medium rounded-lg transition-all duration-300',
                          isActive(item.href) || location.pathname.startsWith('/paths')
                            ? 'text-primary-600 bg-primary-50 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 hover:shadow-sm'
                        )}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {item.name}
                      </Link>
                      
                      {/* Learning Paths Sub-menu */}
                      <div className="ml-4 space-y-1">
                        <div className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">Learning Paths</div>
                        
                        <Link
                          to="/paths/selenium-csharp"
                          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-all duration-300"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          Selenium C# Mastery
                        </Link>
                        
                        <Link
                          to="/paths/playwright"
                          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-all duration-300"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          Playwright Mastery
                        </Link>
                        
                        <Link
                          to="/paths/selenium-java"
                          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-all duration-300"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                          Selenium Java Mastery
                        </Link>
                      </div>
                    </div>
                  );
                }
                
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={cn(
                      'block px-3 py-3 text-base font-medium rounded-lg transition-all duration-300',
                      isActive(item.href)
                        ? 'text-primary-600 bg-primary-50 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 hover:shadow-sm'
                    )}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                );
              })}

              {/* Mobile Learn Paths */}
              <div className="border-t border-gray-200 pt-3 mt-3 space-y-3">
                <div className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                  🚀 Learning Paths
                </div>
                
                {/* Automation Paths */}
                <div className="space-y-2">
                  <div className="px-3 py-1 text-xs font-medium text-gray-600">Automation Testing</div>
                  <Link
                    to="/paths/selenium-csharp"
                    className="flex items-center gap-3 px-3 py-3 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-all duration-300"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <div>
                      <div className="font-medium text-sm">Selenium C# Mastery</div>
                      <div className="text-xs text-gray-500">67,800+ students</div>
                    </div>
                  </Link>
                  <Link
                    to="/paths/selenium-java"
                    className="flex items-center gap-3 px-3 py-3 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-all duration-300"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                    <div>
                      <div className="font-medium text-sm">Selenium Java Mastery</div>
                      <div className="text-xs text-gray-500">78,400+ students</div>
                    </div>
                  </Link>
                  <Link
                    to="/paths/playwright"
                    className="flex items-center gap-3 px-3 py-3 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-all duration-300"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <div>
                      <div className="font-medium text-sm">Playwright Mastery</div>
                      <div className="text-xs text-gray-500">54,200+ students</div>
                    </div>
                  </Link>
                </div>

                {/* AI Paths */}
                <div className="space-y-2 border-t border-gray-100 pt-3">
                  <div className="px-3 py-1 text-xs font-medium text-gray-600">AI & Machine Learning</div>
                  <Link
                    to="/paths"
                    className="flex items-center gap-3 px-3 py-3 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-all duration-300"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                    <div>
                      <div className="font-medium text-sm">AI & Testing Learning Path</div>
                      <div className="text-xs text-gray-500">43,800+ students • Complete Journey</div>
                    </div>
                  </Link>
                </div>

                {/* View All Link */}
                <Link
                  to="/paths"
                  className="group flex items-center justify-center w-full px-4 py-3 text-base font-semibold text-white bg-gradient-to-r from-purple-600 via-blue-600 to-indigo-600 rounded-xl hover:from-purple-700 hover:via-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 relative overflow-hidden"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="flex items-center gap-2 relative z-10">
                    <Zap className="h-5 w-5 animate-pulse" aria-label="Zap icon" />
                    <span>View All Paths</span>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Expandable Search Bar - Desktop */}
      {isSearchOpen && (
        <div className="hidden lg:block bg-white border-t border-gray-200 shadow-lg search-container"
             style={{
               animation: 'slideDown 0.3s ease-out forwards',
               zIndex: 1000
             }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="max-w-2xl mx-auto relative">
              <SearchDropdown className="w-full" aria-label="SearchDropdown icon" />
              {/* Close Button */}
              <button
                onClick={() => setIsSearchOpen(false)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
                aria-label="Close search"
              >
                <X className="h-5 w-5" aria-label="X icon" />
              </button>
            </div>
          </div>
        </div>
      )}
      </header>
    </>
  );
};

export default Header;